// Tier 2 players migration, Phase B: the real intended replacement for the players portion of
// App.jsx's watchDoc mechanism — currently:
//   watchDoc("players", setPlayers, d=>d.players||d.items||SEED.players)
// which subscribes to the single warriors/players document and reads its `players` array field.
// This module instead subscribes to the new top-level `players` collection (one document per
// player, doc ID = the player's numeric id as a string — see scripts/migrate-players.cjs) and
// reconstructs an equivalent in-memory array.
//
// Wired into App.jsx since Phase C+D (imported at the top of that file) — the header comment above
// predates that cutover and was never updated; leaving it as historical context for why this module
// exists, not as a current statement of whether it's in use.
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';

// Converts one players/{id} document into the shape the app's in-memory `players` array has
// always used, with one difference from the raw document, undone here at this boundary:
//  - `id`: Phase A wrote this as a string so it matches the doc ID exactly. Every existing
//    consumer in App.jsx (SEED.players, the _id counter, gearHistory/draftRosters cross-refs,
//    stats lookups, etc.) expects a number, so it's converted back here — not left as a string
//    that happens to look like a number, and not carried through as string with callers patched
//    to compare loosely, which would risk === comparisons silently failing wherever a strict
//    numeric comparison exists.
// `nodeId` USED to be dropped here too ("nothing client-side reads it yet") — that stopped being
// true once org message compose (App.jsx, TARGET NODE(S) control) needed to filter the client's
// own `players` state by nodeId for its recipient preview. Re-checked every write site that
// spreads a player object into a Firestore write (savePlayer, slimPlayer's draftRosters snapshot,
// the gcTeam/GC-stats-import stats patches) — all of them explicitly enumerate the fields they
// write rather than spreading a whole player object, so carrying nodeId through here doesn't leak
// it into any write that shouldn't have it.
export function playersDocToRecord(docId, data) {
  const { id, ...rest } = data;
  const numericId = Number(docId);
  if (Number.isNaN(numericId)) {
    console.warn(`[playersCollection] Non-numeric player doc id "${docId}" — keeping as string`);
    return { ...rest, id: docId };
  }
  return { ...rest, id: numericId };
}

// Live-subscription replacement for watchDoc("players", setPlayers, ...) — same ergonomics
// (takes the setter, returns an unsubscribe function) so Phase C+D can drop this in place of the
// existing watchDoc call with minimal disruption to the surrounding effect.
export function watchPlayersCollection(setter) {
  return onSnapshot(collection(db, 'players'), (snap) => {
    setter(snap.docs.map((d) => playersDocToRecord(d.id, d.data())));
  });
}

// Node-scoped variant (players pilot) — same ergonomics as watchPlayersCollection above, but
// constrained via where('nodeId','in',nodeIds) to a caller-supplied permitted-node list. Used for
// the admin role only; coach/parent keep the unscoped watchPlayersCollection, since neither has a
// node concept yet. Mirrors the players/{playerId} security rule's admin branch exactly — the rule
// is what actually enforces this, this is just what makes the query itself succeed instead of being
// rejected as unprovable (an unconstrained query can never satisfy a rule that depends on
// resource.data.nodeId, regardless of how permissive the caller's actual access is).
// Firestore's 'in' operator caps at 30 comparison values (confirmed empirically) — a non-issue for
// this pilot (a single real node today), but an admin scoped over >30 descendant nodes down the
// line would need this chunked into multiple queries merged client-side.
export function watchPlayersCollectionForNodes(nodeIds, setter) {
  return onSnapshot(query(collection(db, 'players'), where('nodeId', 'in', nodeIds)), (snap) => {
    setter(snap.docs.map((d) => playersDocToRecord(d.id, d.data())));
  });
}
